import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { useEffect, useState, useCallback, memo } from "react";
import { OrderDetail } from "../../models/orders/order-list-model";
import NewItemDeleteDialog from "./new-item-delete-dialog";
import { calculateItemSum, calculateItemTotal, calculateTotalTax } from "../products/product-calculation";
import NewCreateInvoiceTotal from "./new-create-invoice-total";
import { useTranslation } from "react-i18next";
import posService from "../../services/pos/pos.service";
import { Message } from "primereact/message";

const NewCreateInvoiceItems = memo((props: any) => {
  const { t } = useTranslation();
  const [orderData, setOrderData] = useState<OrderDetail[]>([]);
  const [checkoutItem, setCheckoutItem] = useState<OrderDetail | null>(null);
  const [totalTaxable, setTotalTaxable] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);
  const [disAmt, setDisAmt] = useState<number>(0);
  const [stockMap, setStockMap] = useState<Record<number, number | null>>({});


  const calculateItemsTotal = useCallback((items = orderData) => {
    return items.reduce(
      (total, item) => total + item.pendingQuantity * (item.unitPrice || 0),
      0
    );
  }, [orderData]);

  const calculateTotalTaxItems = useCallback((items = orderData) => {
    return items.reduce(
      (total, item) => total + item.pendingQuantity * (item.unitPrice || 0) * (item.gst / 100),
      0
    );
  }, [orderData]);

useEffect(() => {
  const fetchStock = async () => {
    if (!props.orderedItems?.posID || !props.orderedItems?.orderDetails?.length) return;
    const map: Record<number, number | null> = {};
    await Promise.all(
      props.orderedItems.orderDetails.map(async (item: OrderDetail) => {
        try {
          const stockResponse = await posService.GetposporductDetails(
            props.orderedItems.posID,
            item.productID
          );
          const stockInfo = stockResponse?.[0];
          map[item.productID] = stockInfo?.currentStock ?? null;
        } catch {
          map[item.productID] = null;
        }
      })
    );
    setStockMap(map);
  };

  fetchStock();
}, [props.orderedItems?.orderDetails, props.orderedItems?.posID]);


const productNameBodyTemplate = (product: OrderDetail) => {
  const currentStock = stockMap[product.productID];
  const isInsufficient = currentStock !== undefined && currentStock !== null && product.pendingQuantity > currentStock;

  return (
    <div>
      <div className="font-semibold">{product.productName}</div>
      {isInsufficient && (
        <Message
          severity="warn"
          text={`Insufficient stock, current stock is ${currentStock}`}
        />
      )}
    </div>
  );
};

  useEffect(() => {
    if (props.orderedItems?.orderDetails) {
      const mappedData = props.orderedItems.orderDetails.map((item: OrderDetail) => ({
        ...item,
      }));
      setOrderData(mappedData);
    } else {
      setOrderData([]);
    }
  }, [props.orderedItems?.orderDetails]);

  useEffect(() => {
    const taxable = calculateItemsTotal();
    const tax = calculateTotalTaxItems();
    
    if (taxable !== totalTaxable || tax !== totalTax) {
      setTotalTaxable(taxable);
      setTotalTax(tax);
      props.updateItems(orderData, taxable, tax, disAmt);
    }

    const isEmpty = !orderData || orderData.length === 0 || orderData.every(item => item.pendingQuantity === 0);
    props.onOrderDataEmptyChange?.(isEmpty);

  
  const hasInsufficientStock = orderData.some(item => {
    const stock = stockMap[item.productID];
    return stock !== undefined && stock !== null && item.pendingQuantity > stock;
  });
  props.onInsufficientStockChange?.(hasInsufficientStock);

  }, [orderData, disAmt, calculateItemsTotal, calculateTotalTaxItems, stockMap]);

  const updateDiscount = useCallback((value: number) => {
    setDisAmt(value);
    const taxable = calculateItemsTotal();
    const tax = calculateTotalTaxItems();
    props.updateItems(orderData, taxable, tax, value);
  }, [orderData, calculateItemsTotal, calculateTotalTaxItems, props.updateItems]);

  const imageBodyTemplate = (item: any) => {
    return (
      <img
        src={
          item.url === undefined
            ? process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png"
            : item.url
        }
        alt=""
        width="90%"
      />
    );
  };

  const unitPriceBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(product.unitPrice);
  };

  const quantityTemplate = (product: OrderDetail) => {
    return (
      <div className="flex product-item product-wrapper">
        <div className="product-list-quantity">
          <InputNumber
            inputId={String(product.productID)}
            style={{ width: 120 }}
            value={product.pendingQuantity}
            onClick={(e) => updateItemsQuantity(e, product.productID)}
            showButtons
            buttonLayout="horizontal"
            step={product.orderMultiples || 1}
            decrementButtonClassName="p-button-danger"
            className="quantity-border"
            incrementButtonClassName="p-button-success"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            min={product.minOrderQty || 1}
          />
        </div>
        <div className="product-list-button ml-2">
          <Button
            icon="pi pi-trash"
            rounded
            severity="danger"
            onClick={() => deleteItem(product)}
            raised
          />
        </div>
      </div>
    );
  };

  const updateItemsQuantity = (event: any, id: number) => {
    const isIncrement = event.target.dataset.pcSection === "incrementiconprops" || 
                       event.target.dataset.pcSection === "incrementbutton";
    const isDecrement = event.target.dataset.pcSection === "decrementiconprops" || 
                       event.target.dataset.pcSection === "decrementbutton";

    if (!isIncrement && !isDecrement) return;

    setOrderData(prev => {
      return prev.map(item => {
        if (item.productID !== id) return item;
        
        const multiples = item.orderMultiples || 1;
        let newQuantity = item.pendingQuantity || 0;
        
        if (isIncrement) {
          newQuantity += multiples;
        } else if (isDecrement && newQuantity > (item.minOrderQty || 1)) {
          newQuantity = Math.max((item.minOrderQty || 1), newQuantity - multiples);
        }
        
        return { ...item, pendingQuantity: newQuantity };
      });
    });
  };

  const deleteItem = (item: OrderDetail) => {
    setCheckoutItem(item);
    setNotifyHeader("Delete Item");
    setNotifyMessage("Are you sure you want to delete this item?");
    setIsNotifyVisible(true);
  };

  const hideNotifyDialog = (value: boolean) => {
    if (value && checkoutItem) {
      setOrderData(prev => 
        prev.filter(product => product.productID !== checkoutItem.productID)
      );
    }
    setIsNotifyVisible(false);
  };

  const taxableAmountBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(
      calculateItemSum(product.unitPrice, product.pendingQuantity)
    );
  };

  const taxAmountBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(
      calculateTotalTax(product.unitPrice, product.pendingQuantity, product.gst)
    );
  };

  const grossAmountBodyTemplate = (product: OrderDetail) => {
    return Helpers.formatAmountInINR(
      Math.round(
        calculateItemTotal(
          product.unitPrice, 
          product.pendingQuantity, 
          calculateTotalTax(product.unitPrice, product.pendingQuantity, product.gst), 
          0
        )
      )
    );
  };

  return (
    <>
      <DataTable
        value={orderData.length > 0 ? orderData.filter(x => x.pendingQuantity > 0) : []}
        tableStyle={{ minWidth: "50rem" }}
        size="small"
      >
        <Column style={{ width: "5%", marginLeft: "8rem" }} body={imageBodyTemplate}></Column>
        <Column
          field="productName"
          style={{ width: "20%" }}
          header={t("invoices_product_name")}
          body={productNameBodyTemplate}
        ></Column>        
        <Column
          field="unitPrice"
          header={t("invoices_product_price")}
          body={unitPriceBodyTemplate}
          bodyStyle={{ textAlign: "center" }}
        ></Column>
        <Column
          field="quantity"
          style={{ width: "5%"}}
          header={t("invoices_product_quantity")}
          body={quantityTemplate}
        ></Column>
        <Column
          field="taxableAmount"
          header={t("invoices_product_amount")}
          body={taxableAmountBodyTemplate}
          bodyStyle={{ textAlign: "right" }}
        ></Column>
        <Column
          field="taxAmount"
          header={t("invoices_product_tax")}
          body={taxAmountBodyTemplate}
          bodyStyle={{ textAlign: "right" }}
        ></Column>
        <Column
          field="grossAmount"
          header={t("invoices_product_tot_amount")}
          body={grossAmountBodyTemplate}
          bodyStyle={{ textAlign: "right" }}
        ></Column>
      </DataTable>
      <NewItemDeleteDialog
          messageHeader={notifyHeader}
          notifyMessage={notifyMessage}
          isVisible={isNotifyVisible}
          isHide={hideNotifyDialog}
      />
      {totalTaxable > 0 && (       
        <NewCreateInvoiceTotal 
          taxableAmount={totalTaxable} 
          taxAmount={totalTax}
          orderedItems={props.orderedItems}
          updateDiscountAmount={updateDiscount}
        />
      )}
    </>
  );
});

export default NewCreateInvoiceItems;